import axios from 'axios';
import ExclamationMarkIcon from '@/lib/assets/mark.svg';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import CheckIcon from '@/lib/assets/check.svg';
import BackLink from '../BackLink';
import AdyenDonation from '../AdyenDonation';

import styles from './CallbackPaymentBlock.module.scss';

const FIVE_SECONDS = 5 * 1000;
const FIFTEEN_SECONDS = 15 * 1000;

const CallbackPaymentBlock = ({
    isPaymentSuccess,
    redirectURL,
    isTimeUp,
    isCheckoutPlan = false,
    donationEnabled,
    shouldRedirect,
    setShouldRedirect,
    lyriaUrl,
    paymentRequestId,
    paymentRequestToken,
    locale = '',
    errorCode = '',
}) => {
    const [displayDonation, setDisplayDonation] = React.useState(false);
    const [donationFinished, setDonationFinished] = React.useState(false);
    const [donationSuccess, setDonationSuccess] = React.useState(false);
    const delay = isPaymentSuccess && (!displayDonation || donationSuccess) ? FIVE_SECONDS : FIFTEEN_SECONDS;
    const [countdown, setCountdown] = React.useState(delay / 1000);

    const { t } = useTranslation();

    React.useEffect(() => {
        setDisplayDonation(donationEnabled);
    }, [donationEnabled]);

    const onClickRedirect = useCallback(
        () => {
            if (isCheckoutPlan) {
                const url = new URL(redirectURL);
                url.searchParams.set('redirect', 'true');
                window.location.assign(url.toString());
            } else {
                window.location.assign(redirectURL);
            }

        },
        [isCheckoutPlan, redirectURL],
    );

    React.useEffect(() => {
        if (!shouldRedirect) return;

        const interval = setInterval(() => {
            setCountdown((prev) => Math.max(0, prev - 1));
        }, 1000);

        const timeout = setTimeout(onClickRedirect, delay);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, [shouldRedirect, delay, onClickRedirect]);

    const sendErrorToLyria = React.useCallback(({ error_message, response }) => {
        let data = {
            request_token: paymentRequestToken,
            payment_request_id: paymentRequestId,
            error_message: error_message ?? response?.data?.message ?? '',
            query_action: (() => {
                if (!response?.data?.action) return '';
                try {
                    const str = JSON.stringify(response.data.action);
                    return typeof str === 'string' ? str : '';
                } catch (error) {
                    console.error('Failed to stringify action:', error);
                    return '';
                }
            })(),
            url: response ? response?.data?.callback_url : '',
        };
        // if we cannot extract anything useful, just put the whole response's body in the error_message
        if (data['error_message'] === '' && data['query_action'] === '') {
            try {
                data['error_message'] = response.text();
            } catch (error) {
                console.error(error);
            }
        }
        axios({
            method: 'post',
            url: `${lyriaUrl}/payments/error`,
            data: data,
        }).catch((error) => {
            console.error('Failed to send error to Lyria:', error);
        });
    }, [lyriaUrl, paymentRequestId, paymentRequestToken]);


    return (
        <div className={styles.container}>
            {!isPaymentSuccess && isCheckoutPlan && !isTimeUp && (
                <BackLink />
            )}
            {isPaymentSuccess
                ? (
                    <>
                        {donationFinished ? (
                            <>
                                {donationSuccess ? (<CheckIcon alt='check icon' />) : (<div className={styles.semiSuccess}><ExclamationMarkIcon ></ExclamationMarkIcon></div>)}
                                <div className={styles.title}>
                                    {t(donationSuccess ? 'donationSuccess' : 'donationFailure')}
                                </div>
                            </>
                        ) : (
                            <>
                                <CheckIcon alt='check icon' />
                                <div className={styles.title}>{t('callbackValidatedPayment')}</div>
                            </>
                        )}
                        {displayDonation && !donationFinished &&
                            <AdyenDonation
                                isPaymentSuccess={isPaymentSuccess}
                                locale={locale}
                                lyriaUrl={lyriaUrl}
                                paymentRequestId={paymentRequestId}
                                donationEnabled={donationEnabled}
                                shouldRedirect={shouldRedirect}
                                setShouldRedirect={setShouldRedirect}
                                displayDonation={displayDonation}
                                setDisplayDonation={setDisplayDonation}
                                setDonationSuccess={setDonationSuccess}
                                setDonationFinished={setDonationFinished}
                                sendErrorToLyria={sendErrorToLyria}
                            />
                        }

                    </>
                )
                :
                (
                    <div>
                        {errorCode === 'order_is_cancelled'
                            ? t('orderCancelled')
                            : (isTimeUp ? t('order_expired') : t('callbackFailedPayment'))}
                    </div>
                )}
            {redirectURL && shouldRedirect &&
                <>
                    <button className={styles.button} onClick={onClickRedirect}>{t('callbackCTA')}</button>
                    <p className={styles.footer}>{t('callbackFooter', { count: countdown })}</p>
                </>
            }
        </div>
    );
};

CallbackPaymentBlock.propTypes = {
    isPaymentSuccess: PropTypes.bool,
    redirectURL: PropTypes.string,
    isTimeUp: PropTypes.bool,
    isCheckoutPlan: PropTypes.bool,
    paymentRequestId: PropTypes.string,
    errorCode: PropTypes.string,
};

export default CallbackPaymentBlock;
